//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Close, Copy, CheckmarkFilled, ErrorFilled } from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { useUserAuth } from '../../UserAuth';
import AiDataService from '../../utils/AiDataService';
import CustomSnackbar, { useSnackbar } from '../common/CustomSnackbar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../ui/accordion';

interface EditConnectionData {
  connection_id: string;
  teamspace_id: string;
  name: string;
  webhook_url: string;
  status: string;
  has_signing_secret: boolean;
  has_bot_token: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface SlackConnectionWizardProps {
  isVisible: boolean;
  onClose: () => void;
  editConnection?: EditConnectionData | null;
}

interface ConnectionResponse {
  connection_id: string;
  webhook_url: string;
  manifest: Record<string, unknown>;
  status: string;
}

const REQUIRED_BOT_SCOPES = [
  'app_mentions:read',
  'chat:write',
  'chat:write.public',
  'users:read',
  'im:history',
  'channels:history',
  'groups:history',
  'mpim:history',
  'channels:read',
];
const REQUIRED_BOT_EVENTS = ['app_mention', 'message.im'];

function buildManifest(
  webhookUrl: string,
  name: string,
  interactiveUrl: string = '',
): Record<string, unknown> {
  return {
    display_information: { name },
    features: {
      bot_user: { display_name: 'loopgenie', always_online: false },
    },
    oauth_config: { scopes: { bot: REQUIRED_BOT_SCOPES } },
    settings: {
      event_subscriptions: {
        request_url: webhookUrl,
        bot_events: REQUIRED_BOT_EVENTS,
      },
      interactivity: {
        is_enabled: !!interactiveUrl,
        request_url: interactiveUrl,
      },
      org_deploy_enabled: false,
      socket_mode_enabled: false,
      token_rotation_enabled: false,
    },
  };
}

function deriveInteractiveUrl(webhookUrl: string): string {
  return webhookUrl.replace('/slack/events/', '/slack/interactive/');
}

/** Returns true if the string contains any ASCII control character (0x00–0x1F or 0x7F). */
function hasControlCharacters(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 31 || code === 127) return true;
  }
  return false;
}

/**
 * Loose signing-secret format check.
 * Slack's observed format is a 32-char hex string, but we accept 24–64 hex
 * chars to avoid hard-blocking if Slack ever changes the length.
 */
function validateSigningSecret(
  raw: string,
  t: (key: string) => string,
): string | null {
  const trimmed = raw.trim();
  if (!trimmed)
    return t('signing-secret-required') || 'Signing secret is required';
  if (!/^[a-fA-F0-9]+$/.test(trimmed)) {
    return (
      t('signing-secret-hex-only') ||
      'Signing secret must contain only hex characters (0-9, a-f)'
    );
  }
  if (trimmed.length < 24 || trimmed.length > 64) {
    return (
      t('signing-secret-length') ||
      'Signing secret should be between 24 and 64 characters'
    );
  }
  return null;
}

/**
 * Light bot-token format check.
 * Must start with `xoxb-` and be 50–200 chars.  Rejects common wrong-token
 * prefixes with a targeted message so users know what they pasted wrong.
 */
function validateBotToken(
  raw: string,
  t: (key: string) => string,
): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return t('bot-token-required') || 'Bot token is required';

  if (trimmed.startsWith('xoxp-')) {
    return (
      t('bot-token-wrong-prefix-user') ||
      'This looks like a user token (xoxp-). Please use a bot token (xoxb-...)'
    );
  }
  if (trimmed.startsWith('xapp-')) {
    return (
      t('bot-token-wrong-prefix-app') ||
      'This looks like an app-level token (xapp-). Please use a bot token (xoxb-...)'
    );
  }
  if (trimmed.startsWith('xoxe-') || trimmed.startsWith('xoxe.xoxp-')) {
    return (
      t('bot-token-wrong-prefix-refresh') ||
      'This looks like a refresh/config token. Please use a bot token (xoxb-...)'
    );
  }
  if (!trimmed.startsWith('xoxb-')) {
    return t('bot-token-invalid-prefix') || 'Bot token must start with xoxb-';
  }
  if (trimmed.length < 50 || trimmed.length > 200) {
    return (
      t('bot-token-length') ||
      'Bot token should be between 50 and 200 characters'
    );
  }
  return null;
}

const SlackConnectionWizard: React.FC<SlackConnectionWizardProps> = ({
  isVisible,
  onClose,
  editConnection,
}) => {
  const { t } = useTranslation();
  const { teamspaceId } = useTeamspaceContext();
  const { user, isAuthenticated } = useUserAuth();
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step 1
  const [connectionName, setConnectionName] = useState('');
  const [nameError, setNameError] = useState('');
  const [connectionData, setConnectionData] =
    useState<ConnectionResponse | null>(null);

  // Step 2
  const [signingSecret, setSigningSecret] = useState('');
  const [botToken, setBotToken] = useState('');
  const [signingSecretSaved, setSigningSecretSaved] = useState(false);
  const [botTokenSaved, setBotTokenSaved] = useState(false);
  const [signingSecretError, setSigningSecretError] = useState('');
  const [botTokenError, setBotTokenError] = useState('');

  const [copiedField, setCopiedField] = useState<string | null>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const userRef = useRef(user);
  const isAuthRef = useRef(isAuthenticated);
  userRef.current = user;
  isAuthRef.current = isAuthenticated;

  const aiDataService = useMemo(
    () =>
      new AiDataService(
        () => userRef.current,
        () => isAuthRef.current,
      ),
    [],
  );

  const handleCopy = (text: string, field: string) => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedField(field);
        copyTimerRef.current = setTimeout(() => setCopiedField(null), 2000);
      })
      .catch(() => {
        /* clipboard write failed — no feedback shown */
      });
  };

  const handleCreateConnection = async () => {
    const trimmedName = connectionName.trim();
    if (!trimmedName) {
      setNameError(t('name-required') || 'Name is required');
      return;
    }
    if (trimmedName.length > 35) {
      setNameError(
        t('name-max-length') || 'Name must be 35 characters or less',
      );
      return;
    }
    if (hasControlCharacters(trimmedName)) {
      setNameError(
        t('name-no-control-chars') ||
          'Name contains unsupported invisible characters. Try retyping it instead of pasting.',
      );
      return;
    }
    setNameError('');
    setIsSubmitting(true);

    try {
      const response = await aiDataService.createSlackConnection(
        teamspaceId || '',
        { name: connectionName.trim() },
      );
      setConnectionData(response as unknown as ConnectionResponse);
      setStep(2);
    } catch (error) {
      showSnackbar(
        t('error-creating-slack-connection') ||
          'Error creating Slack connection',
        'error',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveSigningSecret = async () => {
    if (!connectionData) return;
    const validationError = validateSigningSecret(signingSecret, t);
    if (validationError) {
      setSigningSecretError(validationError);
      return;
    }
    setIsSubmitting(true);
    setSigningSecretError('');
    try {
      await aiDataService.saveSlackSigningSecret(
        teamspaceId || '',
        connectionData.connection_id,
        signingSecret.trim(),
      );
      setSigningSecretSaved(true);
      showSnackbar(
        t('signing-secret-saved') || 'Signing secret saved',
        'success',
      );
    } catch (error) {
      setSigningSecretSaved(false);
      setSigningSecretError(
        t('error-saving-signing-secret') || 'Error saving signing secret',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveBotToken = async () => {
    if (!connectionData) return;
    const validationError = validateBotToken(botToken, t);
    if (validationError) {
      setBotTokenError(validationError);
      return;
    }
    setIsSubmitting(true);
    setBotTokenError('');
    try {
      await aiDataService.saveSlackBotToken(
        teamspaceId || '',
        connectionData.connection_id,
        botToken.trim(),
      );
      setBotTokenSaved(true);
      showSnackbar(t('bot-token-saved') || 'Bot token saved', 'success');
    } catch (error) {
      setBotTokenSaved(false);
      setBotTokenError(t('error-saving-bot-token') || 'Error saving bot token');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinish = () => {
    if (signingSecretSaved && botTokenSaved) {
      setStep(3);
    }
  };

  const handleCopyManifest = () => {
    if (!connectionData?.manifest) return;
    handleCopy(JSON.stringify(connectionData.manifest, null, 2), 'manifest');
  };

  const isEditMode = !!editConnection;

  useEffect(() => {
    if (isVisible && editConnection) {
      const webhookUrl = editConnection.webhook_url;
      const interactiveUrl = deriveInteractiveUrl(webhookUrl);
      const manifest = buildManifest(
        webhookUrl,
        editConnection.name,
        interactiveUrl,
      );

      setConnectionName(editConnection.name);
      setConnectionData({
        connection_id: editConnection.connection_id,
        webhook_url: webhookUrl,
        manifest,
        status: editConnection.status,
      });
      setSigningSecretSaved(editConnection.has_signing_secret);
      setBotTokenSaved(editConnection.has_bot_token);
      setStep(2);

      aiDataService
        .getSlackConnection(teamspaceId || '', editConnection.connection_id)
        .then((fullData: any) => {
          if (fullData?.webhook_url || fullData?.manifest) {
            const updatedUrl = fullData.webhook_url || webhookUrl;
            const updatedInteractiveUrl = deriveInteractiveUrl(updatedUrl);
            setConnectionData((prev) => ({
              connection_id:
                fullData.connection_id || prev?.connection_id || '',
              webhook_url: updatedUrl,
              manifest:
                fullData.manifest ||
                buildManifest(
                  updatedUrl,
                  editConnection.name,
                  updatedInteractiveUrl,
                ),
              status: fullData.status || prev?.status || '',
            }));
          }
        })
        .catch(() => {
          /* best-effort — the panel already has the locally-built manifest */
        });
    }
  }, [isVisible, editConnection, teamspaceId, aiDataService]);

  const handleClose = () => {
    if (copyTimerRef.current) {
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = null;
    }
    setStep(1);
    setConnectionName('');
    setNameError('');
    setConnectionData(null);
    setSigningSecret('');
    setBotToken('');
    setSigningSecretSaved(false);
    setBotTokenSaved(false);
    setSigningSecretError('');
    setBotTokenError('');
    onClose();
  };

  const stepperItems = [
    { num: 1, label: t('create-connection') || 'Create Connection' },
    { num: 2, label: t('configure-credentials') || 'Configure Credentials' },
    { num: 3, label: t('setup-complete') || 'Setup Complete' },
  ];

  const getStepCircleClass = (s: number) => {
    if (step === s) return 'bg-brand-blue text-white';
    if (step > s) return 'bg-brand-blue text-white';
    return 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-400';
  };

  const getStepLabelClass = (s: number) => {
    if (step === s) return 'font-semibold text-gray-900 dark:text-white';
    if (step > s) return 'text-gray-700 dark:text-gray-300';
    return 'text-gray-500 dark:text-gray-400';
  };

  if (!isVisible) return null;

  return (
    <>
      <div
        data-testid="slack-wizard-backdrop"
        className="fixed inset-0 top-12 z-40 bg-black/20"
        onClick={handleClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') handleClose();
        }}
        role="presentation"
      />
      <CustomSnackbar
        open={snackbarState.open}
        message={snackbarState.message}
        severity={snackbarState.severity}
        onClose={closeSnackbar}
      />
      <div
        data-testid="slack-wizard-container"
        className="fixed bottom-0 right-0 top-12 z-50 flex w-[560px] flex-col border-l border-[#d3d9de] bg-white shadow-xl dark:border-gray-700 dark:bg-[#262626]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 bg-[#f2f4f5] p-4 dark:border-gray-700 dark:bg-[#393939]">
          <h2 className="text-[16px] font-semibold text-gray-700 dark:text-gray-300">
            {isEditMode
              ? t('edit-slack-integration') || 'Edit Slack Integration'
              : t('add-slack-integration') || 'Add Slack Integration'}
          </h2>
          <button
            onClick={handleClose}
            className="rounded p-1 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600"
            aria-label="Close"
          >
            <Close size={16} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-3">
          {/* Horizontal Stepper */}
          <div className="mb-6 flex">
            {stepperItems.map((s, i) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-1 flex-col">
                  <div
                    className={`mb-3 h-1 w-full rounded-full ${
                      step >= s.num
                        ? 'bg-brand-blue'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                  <div className="flex items-center gap-2">
                    {step > s.num ? (
                      <CheckmarkFilled
                        size={24}
                        className="shrink-0 text-brand-blue"
                      />
                    ) : (
                      <span
                        className={`flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${getStepCircleClass(s.num)}`}
                      >
                        {s.num}
                      </span>
                    )}
                    <span
                      className={`whitespace-nowrap text-xs font-medium ${getStepLabelClass(s.num)}`}
                    >
                      {s.label}
                    </span>
                  </div>
                </div>
                {i < 2 && <div className="w-2 shrink-0" />}
              </React.Fragment>
            ))}
          </div>
          {/* Step Title */}
          {step !== 3 && (
            <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
              {stepperItems.find((s) => s.num === step)?.label}
            </h3>
          )}

          {/* Step 1: Create Connection */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('slack-connection-description') ||
                    'Enter a name for this Slack connection. This will generate a webhook URL and app manifest for your Slack app.'}
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('connection-name') || 'Connection Name'} *
                </label>
                <Input
                  type="text"
                  value={connectionName}
                  maxLength={35}
                  onChange={(e) => {
                    setConnectionName(e.target.value);
                    setNameError('');
                  }}
                  placeholder={
                    t('enter-connection-name') || 'Enter connection name'
                  }
                  className={`border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-[#393939] dark:text-white ${nameError ? 'border-red-500' : ''}`}
                />
                <div className="mt-1 flex items-center justify-between">
                  {nameError ? (
                    <p className="text-xs text-red-500">{nameError}</p>
                  ) : (
                    <span />
                  )}
                  <span
                    className={`text-xs ${
                      connectionName.trim().length > 35
                        ? 'text-red-500'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {connectionName.trim().length}/35
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={handleClose}>
                  {t('cancel') || 'Cancel'}
                </Button>
                <Button
                  data-testid="create-connection-button"
                  onClick={handleCreateConnection}
                  disabled={isSubmitting || !connectionName.trim()}
                  className="bg-brand-blue text-white hover:bg-brand-nav dark:bg-brand-blue dark:hover:bg-brand-interactiveDark"
                >
                  {isSubmitting
                    ? t('creating') || 'Creating...'
                    : t('create-connection') || 'Create Connection'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Configure Slack App & Enter Credentials */}
          {step === 2 && connectionData && (
            <div className="space-y-6">
              {/* Instructions Accordion */}
              <Accordion type="single" collapsible>
                <AccordionItem
                  value="instructions"
                  className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20"
                >
                  <AccordionTrigger className="px-4 text-sm font-semibold text-blue-900 dark:text-blue-200">
                    {t('setup-instructions') || 'Setup Instructions'}
                  </AccordionTrigger>
                  <AccordionContent className="px-4">
                    <ol className="list-inside list-decimal space-y-2 text-sm text-blue-800 dark:text-blue-300">
                      <li>
                        {t('slack-step-1') ||
                          'Download the manifest below and create a new Slack app at api.slack.com → Create New App → From Manifest'}
                      </li>
                      <li>
                        {t('slack-step-2') ||
                          "Copy the Webhook URL below and paste it into your Slack app's Event Subscriptions → Request URL"}
                      </li>
                      <li>
                        {t('slack-step-3') ||
                          'From your Slack app → Basic Information → App Credentials, copy the Signing Secret and paste it below'}
                      </li>
                      <li>
                        {t('slack-step-4') ||
                          'Install the app to your workspace, then copy the Bot User OAuth Token from OAuth & Permissions and paste it below'}
                      </li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Webhook URL */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('webhook-url') || 'Webhook URL'}
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      handleCopy(connectionData.webhook_url, 'webhook')
                    }
                    className="shrink-0"
                  >
                    {copiedField === 'webhook' ? (
                      <CheckmarkFilled size={16} className="text-green-600" />
                    ) : (
                      <Copy
                        size={16}
                        className="text-gray-500 dark:text-gray-400"
                      />
                    )}
                  </Button>
                </div>
                <Input
                  type="text"
                  value={connectionData.webhook_url}
                  readOnly
                  className="w-full border-gray-300 bg-gray-50 font-mono text-xs text-gray-900 dark:border-gray-600 dark:bg-[#393939] dark:text-white"
                />
              </div>

              {/* Manifest - Scrollable Textbox */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('app-manifest') || 'App Manifest'}
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyManifest}
                    className="shrink-0"
                  >
                    {copiedField === 'manifest' ? (
                      <CheckmarkFilled size={16} className="text-green-600" />
                    ) : (
                      <Copy
                        size={16}
                        className="text-gray-500 dark:text-gray-400"
                      />
                    )}
                  </Button>
                </div>
                <textarea
                  readOnly
                  value={
                    connectionData?.manifest
                      ? JSON.stringify(connectionData.manifest, null, 2)
                      : ''
                  }
                  className="h-48 w-full resize-none rounded-md border border-gray-300 bg-gray-50 p-3 font-mono text-xs text-gray-900 focus:outline-none dark:border-gray-600 dark:bg-[#393939] dark:text-white"
                />
              </div>

              <hr className="border-gray-200 dark:border-gray-700" />

              {/* Signing Secret */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('signing-secret') || 'Signing Secret'} *
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input
                      type="password"
                      value={signingSecret}
                      onChange={(e) => {
                        setSigningSecret(e.target.value);
                        if (signingSecretError) setSigningSecretError('');
                      }}
                      placeholder={
                        isEditMode && signingSecretSaved && !signingSecret
                          ? '••••••••••••••••'
                          : t('enter-signing-secret') ||
                            'Paste signing secret from Slack'
                      }
                      className="border-gray-300 bg-white pr-8 text-gray-900 dark:border-gray-600 dark:bg-[#393939] dark:text-white"
                    />
                    {signingSecretSaved && !signingSecretError && (
                      <CheckmarkFilled
                        size={16}
                        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-green-500"
                      />
                    )}
                    {signingSecretError && (
                      <ErrorFilled
                        size={16}
                        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-red-500"
                      />
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={handleSaveSigningSecret}
                    disabled={isSubmitting || !signingSecret.trim()}
                    className="shrink-0 bg-brand-blue text-white hover:bg-brand-nav"
                  >
                    {isEditMode && signingSecretSaved && !signingSecret
                      ? t('update') || 'Update'
                      : t('add') || 'Add'}
                  </Button>
                </div>
                {signingSecretError && (
                  <p className="mt-1 text-xs text-red-500">
                    {signingSecretError}
                  </p>
                )}
              </div>

              {/* Bot Token */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('bot-token') || 'Bot Token'} *
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input
                      type="password"
                      value={botToken}
                      onChange={(e) => {
                        setBotToken(e.target.value);
                        if (botTokenError) setBotTokenError('');
                      }}
                      placeholder={
                        isEditMode && botTokenSaved && !botToken
                          ? '••••••••••••••••'
                          : t('enter-bot-token') || 'Paste bot token (xoxb-...)'
                      }
                      className="border-gray-300 bg-white pr-8 text-gray-900 dark:border-gray-600 dark:bg-[#393939] dark:text-white"
                    />
                    {botTokenSaved && !botTokenError && (
                      <CheckmarkFilled
                        size={16}
                        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-green-500"
                      />
                    )}
                    {botTokenError && (
                      <ErrorFilled
                        size={16}
                        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-red-500"
                      />
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={handleSaveBotToken}
                    disabled={isSubmitting || !botToken.trim()}
                    className="shrink-0 bg-brand-blue text-white hover:bg-brand-nav"
                  >
                    {isEditMode && botTokenSaved && !botToken
                      ? t('update') || 'Update'
                      : t('add') || 'Add'}
                  </Button>
                </div>
                {botTokenError && (
                  <p className="mt-1 text-xs text-red-500">{botTokenError}</p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={handleClose}>
                  {t('cancel') || 'Cancel'}
                </Button>
                {isEditMode ? (
                  <Button
                    onClick={handleClose}
                    className="bg-brand-blue text-white hover:bg-brand-nav dark:bg-brand-blue dark:hover:bg-brand-interactiveDark"
                  >
                    {t('done') || 'Done'}
                  </Button>
                ) : (
                  <Button
                    onClick={handleFinish}
                    disabled={!signingSecretSaved || !botTokenSaved}
                    className="bg-brand-blue text-white hover:bg-brand-nav dark:bg-brand-blue dark:hover:bg-brand-interactiveDark"
                  >
                    {t('save') || 'Save'}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Complete */}
          {step === 3 && (
            <div className="flex flex-col items-center justify-center space-y-6 py-12">
              <CheckmarkFilled size={48} className="text-green-600" />
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('slack-connection-complete') ||
                    'Slack Connection Complete'}
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {t('slack-connection-complete-description') ||
                    `Your Slack connection "${connectionName}" has been configured successfully. The connection will become active once Slack verifies the webhook URL.`}
                </p>
              </div>
              <Button
                onClick={handleClose}
                className="bg-brand-blue text-white hover:bg-brand-nav dark:bg-brand-blue dark:hover:bg-brand-interactiveDark"
              >
                {t('done') || 'Done'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SlackConnectionWizard;
